/** 图片参考分布：[建筑][1★至5★]，每格为整关25格的占比，单位0.01%。 */
export const BUILDING_STAR_WEIGHTS = [
  [124, 150, 178, 310, 387],
  [170, 205, 245, 426, 535],
  [212, 255, 304, 530, 661],
  [232, 279, 333, 578, 726],
  [341, 411, 491, 852, 1065],
] as const;

export function defaultBuildingSeed(identity: string | number): number {
  let hash = 2166136261;
  for (const char of String(identity).replace(/^L(?=\d+$)/, "")) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return hash >>> 0;
}

function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let x = Math.imul(state ^ (state >>> 15), state | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** 返回 [建筑][星级]；排对应星级，列对应建筑。0 为默认随机，-1 为未打乱的比例表。 */
export function buildingWeights(n: number, m: number, identity: string | number, seed = 0): number[][] {
  if (!Number.isInteger(n) || n < 1 || !Number.isInteger(m) || m < 1) throw new RangeError('建筑和星级数须为正整数');
  if (!Number.isInteger(seed) || seed < -1 || seed > 2147483647) throw new RangeError('造价随机种子无效');
  const grid = Array.from({ length: n }, (_, b) => Array.from({ length: m }, (_, l) => {
    if (m === 1) return 10000;
    const x = l * 4 / (m - 1), i = Math.min(3, Math.floor(x));
    const shape = BUILDING_STAR_WEIGHTS[b % 5];
    return Math.round(shape[i] + (shape[i + 1] - shape[i]) * (x - i));
  }));
  if (seed === -1) return grid;
  const rnd = seededRandom(seed || defaultBuildingSeed(identity));
  const rowSwap = (a: number, b: number) => { if (rnd() < 0.5) for (const column of grid) [column[a], column[b]] = [column[b], column[a]]; };
  const colSwap = (a: number, b: number) => { if (rnd() < 0.5) [grid[a], grid[b]] = [grid[b], grid[a]]; };
  if (m >= 2) rowSwap(0, 1);
  if (m >= 4) rowSwap(m - 2, m - 1);
  if (n >= 2) colSwap(0, 1);
  if (n >= 4) colSwap(n - 2, n - 1);
  const shuffle = (values: number[]) => {
    for (let i = values.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [values[i], values[j]] = [values[j], values[i]]; }
    return values;
  };
  const middleB = Math.floor((n - 1) / 2), middleL = Math.floor((m - 1) / 2);
  grid[middleB] = shuffle(grid[middleB]);
  const middle = shuffle(grid.map((column) => column[middleL]));
  grid.forEach((column, b) => { column[middleL] = middle[b]; });
  return grid;
}

/** 每格至少 1；最大余数法分配整数尾差，逐格合计严格等于目标，不使用浮点金币。 */
export function allocateBuildingCosts(total: bigint, weights: readonly (readonly number[])[]): bigint[][] {
  const flat = weights.flatMap((column) => [...column]);
  if (!flat.length || !flat.every((v) => Number.isSafeInteger(v) && v > 0) || !weights.every((c) => c.length === weights[0].length)) throw new RangeError('建筑比例矩阵无效');
  if (total < BigInt(flat.length)) throw new RangeError('总造价不足以为每个升级格分配至少1金币');
  const sum = flat.reduce((a, v) => a + BigInt(v), 0n), rest = total - BigInt(flat.length);
  const amounts = flat.map((v) => rest * BigInt(v) / sum + 1n);
  let remain = total - amounts.reduce((a, v) => a + v, 0n);
  const order = flat.map((v, i) => ({ i, remainder: rest * BigInt(v) % sum }))
    .sort((a, b) => a.remainder === b.remainder ? a.i - b.i : a.remainder > b.remainder ? -1 : 1);
  for (const entry of order) { if (!remain) break; amounts[entry.i]++; remain--; }
  let offset = 0;
  return weights.map((column) => column.map(() => amounts[offset++]));
}
